import React from 'react';
import { commentIsHiddenPendingReview } from '../../lib/collections/comments/helpers';
import { postGetPageUrl } from '../../lib/collections/posts/helpers';
import { commentPermalinkStyleSetting } from '@/lib/instanceSettings';
import { scrollFocusOnElement } from '@/lib/scrollUtils';
import { useQuery } from "@/lib/crud/useQuery";
import { gql } from "@/lib/generated/gql-codegen";
import Loading from "../vulcan-core/Loading";
import Divider from "../common/Divider";
import CommentOnPostWithReplies from "./CommentOnPostWithReplies";
import CommentWithReplies from "./CommentWithReplies";
import { useStyles } from '../hooks/useStyles';
import { defineStyles } from '../hooks/defineStyles';
import { useCurrentUserId } from '../common/withUser';

const CommentWithRepliesFragmentQuery = gql(`
  query CommentPermalink($documentId: String) {
    comment(input: { selector: { documentId: $documentId } }) {
      result {
        ...CommentWithRepliesFragment
      }
    }
  }
`);

const styles = defineStyles("CommentPermalink", (theme: ThemeType) => ({
  root: {
    ...theme.typography.body2,
    ...theme.typography.commentStyle,
    marginTop: 64
  },
  dividerMargins: {
    marginTop: 150,
    marginBottom: 150,
  },
  permalinkLabel: {
    color: theme.palette.grey[600],
    marginBottom: 16,
    marginLeft: 10,
    [theme.breakpoints.down('md')]: {
      marginTop: 16
    }
  },
  seeInContext: {
    textAlign: "right",
    color: theme.palette.lwTertiary.main,
    marginRight: 10
  },
}))

const CommentPermalink = ({
  documentId,
  post,
  silentLoading=false,
}: {
  documentId: string,
  post?: PostsBase,
  silentLoading?: boolean,
}) => {
  const classes = useStyles(styles);
  const currentUserId = useCurrentUserId();
  const hasInContextComments = commentPermalinkStyleSetting.get() === 'in-context'

  const { data, loading, error, refetch } = useQuery(CommentWithRepliesFragmentQuery, {
    variables: { documentId: documentId },
  });
  const comment = data?.comment?.result;

  if (silentLoading && !comment) return null;

  if (error || (!comment && !loading)) return (
    <div>Comment not found</div>
  )
  
  if (loading) return <Loading />

  if (!comment || !documentId) return null
  
  const hiddenPendingReview = commentIsHiddenPendingReview(comment) && !comment.rejected;
  const isOwnUnreviewedComment = hiddenPendingReview && currentUserId === comment.userId;

  // if the site is currently hiding comments by unreviewed authors, check if we need to hide this comment
  if (hiddenPendingReview && !isOwnUnreviewedComment) return <div className={classes.root}>
    <div className={classes.permalinkLabel}>
      Comment Permalink 
      <p>Error: Sorry, this comment is hidden</p>
    </div>
    <div className={classes.dividerMargins}>
      <Divider />
    </div>
  </div>

  const commentNodeProps = {
    treeOptions: {
      refetch,
      showPostTitle: false,
    },
    forceUnTruncated: true,
    forceUnCollapsed: true,
    noAutoScroll: true
  };
  
  // If there's a `?commentId=...` in the URL for a comment but the comment is
  // on a different post, don't show it. This mostly mitigates the effect when
  // a bug makes a link that fails to clear the ?commentId query term from a
  // URL, and also makes it so you can't construct tricky links that make a
  // comment look like a reply to a post that it isn't a reply to.
  if (post && (comment.postId !== post._id)) {
    return null;
  }

  // NB: classes.root is not in the above styles, but is used by eaTheme
  return (
    <div className={classes.root}>
      <div className={classes.permalinkLabel}>Comment Permalink</div>
      <div>
        {post ? (
          <CommentOnPostWithReplies
            key={comment._id}
            post={post}
            comment={comment}
            commentNodeProps={commentNodeProps}
          />
        ) : (
          <CommentWithReplies
            key={comment._id}
            comment={comment}
            commentNodeProps={commentNodeProps}
            initialMaxChildren={5}
          />
        )}
        <div className={classes.seeInContext}>
          <a href={`#${documentId}`} onClick={(e) => {
            if (!hasInContextComments) return;

            scrollFocusOnElement({ id: comment._id, options: { behavior: "smooth" } });
            e.preventDefault()
          }}>See in context</a>
        </div>
      </div>
      <div className={classes.dividerMargins}>
        <Divider />
      </div>
    </div>
  );
}

export default CommentPermalink;



